import React, { useState, useEffect, useRef } from 'react'
import { createChart, CrosshairMode } from 'lightweight-charts'
import dayjs from 'dayjs'
import { formattedNum } from '../../../utils/info'
import { usePrevious } from 'react-use'
import styled from 'styled-components'
import { Play } from 'react-feather'
import { useIsDarkMode } from '../../../state/user/hooks'
import ReactDOM from 'react-dom'

const IconWrapper = styled.div`
  position: absolute;
  right: 10px;
  color: ${({ theme }) => theme.text1}
  border-radius: 3px;
  height: 16px;
  width: 16px;
  padding: 0px;
  bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  :hover {
    cursor: pointer;
    opacity: 0.7;
  }
`

const CandleStickChart = ({
  dataFrequency,
  data,
  width,
  height = 300,
  base,
  margin = true,
  valueFormatter = (val) => formattedNum(val, true),
}) => {
  // reference for DOM element to create with chart
  const ref = useRef()
  // const chartRef = useRef(null)

  const formattedData = data?.map((entry) => {
    return {
      time: parseFloat(entry.timestamp),
      open: parseFloat(entry.open),
      low: parseFloat(entry.open),
      close: parseFloat(entry.close),
      high: parseFloat(entry.close),
    }
  })

  if (formattedData && formattedData.length > 0) {
    formattedData.push({
      time: dayjs().unix(),
      open: parseFloat(formattedData[formattedData.length - 1].close),
      close: parseFloat(base),
      low: Math.min(parseFloat(base), parseFloat(formattedData[formattedData.length - 1].close)),
      high: Math.max(parseFloat(base), parseFloat(formattedData[formattedData.length - 1].close)),
    })
  }

  // pointer to the chart object
  const [chartCreated, setChartCreated] = useState(false)
  const dataPrev = usePrevious(data)
  const frequencyPrev = usePrevious(dataFrequency)

  const darkMode = useIsDarkMode()
  const textColor = darkMode ? 'white' : 'black'
  const previousTheme = usePrevious(darkMode)

  useEffect(() => {
    if (chartCreated && dataFrequency !== frequencyPrev) {
      updateTimeScaleOptions(dataFrequency)
    }
  // eslint-disable-next-line
  }, [chartCreated, dataFrequency, frequencyPrev])

  const updateTimeScaleOptions = (timeframe) => {
    if (!chartCreated) return

    if (timeframe === 'hour') {
      chartCreated.applyOptions({
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          tickMarkFormatter: (timestamp) => {
            return dayjs.unix(timestamp).utc().format('HH:mm'); // Display only HH:mm for hours
          }
        }
      })
    } else if (timeframe === 'day') {
      chartCreated.applyOptions({
        timeScale: {
          timeVisible: false, // Only display dates
          tickMarkFormatter: (timestamp) => {
            return dayjs.unix(timestamp).utc().format('MMM D'); // Example: Feb 26
          }
        }
      })
    }
  }

  // reset the chart if theme switches
  useEffect(() => {
    if (chartCreated && previousTheme !== darkMode) {
      // remove the tooltip element
      let tooltip = document.getElementById('tooltip-id')
      let node = document.getElementById('test-id')
      node.removeChild(tooltip)
      chartCreated.resize(0, 0)
      setChartCreated()
    }
  }, [chartCreated, darkMode, previousTheme])

  useEffect(() => {
    if (data !== dataPrev && chartCreated) {
      // remove the tooltip element
      let tooltip = document.getElementById('tooltip-id')
      let node = document.getElementById('test-id')
      node.removeChild(tooltip)
      chartCreated.resize(0, 0)
      setChartCreated()
    }
  }, [chartCreated, data, dataPrev])

  // if no chart created yet, create one with options and add to DOM manually
  useEffect(() => {
    if (chartCreated === false) {
      const chart = createChart(ref.current, {
        width: width,
        height: height,
        layout: {
          backgroundColor: 'transparent',
          textColor: textColor,
          fontSize: 14
        },
        grid: {
          vertLines: {
            color: 'rgba(197, 203, 206, 0.5)'
          },
          horzLines: {
            color: 'rgba(197, 203, 206, 0.5)'
          }
        },
        crosshair: {
          mode: CrosshairMode.Normal
        },
        rightPriceScale: {
          borderColor: 'rgba(197, 203, 206, 0.8)',
          visible: true
        },
        timeScale: {
          borderColor: 'rgba(197, 203, 206, 0.8)'
        },
        localization: {
          priceFormatter: val => `USDT ${formattedNum(val)}`
        }
      })

      var candleSeries = chart.addCandlestickSeries({
        upColor: 'green',
        downColor: 'red',
        borderDownColor: 'red',
        borderUpColor: 'green',
        wickDownColor: 'red',
        wickUpColor: 'green',
      })

      candleSeries.setData(formattedData)

      var toolTip = document.createElement('div')
      toolTip.setAttribute('id', 'tooltip-id')
      toolTip.className = 'three-line-legend'
      ref.current.appendChild(toolTip)
      toolTip.style.display = 'flex'
      toolTip.style.flexDirection = 'row'
      toolTip.style.alignItems = 'center'
      toolTip.style.left = (margin ? 116 : 10) + 'px'
      toolTip.style.top = 50 + 'px'
      toolTip.style.backgroundColor = 'transparent'

      function setLastBarText() {
        if (!base) {
          toolTip.innerHTML = ''
          return
        }

        const formattedBase = formattedNum(base, true)

        toolTip.innerHTML = ''

        const formattedDiv = document.createElement('div')
        formattedDiv.style.fontSize = '22px'
        formattedDiv.style.margin = '4px 0px'
        formattedDiv.style.color = textColor

        if (typeof formattedBase === 'string') {
          formattedDiv.innerHTML = formattedBase
        } else {
          ReactDOM.render(formattedBase, formattedDiv)
        }

        toolTip.appendChild(formattedDiv)
      }

      function formatTimestampToTime(timestamp) {
        return dayjs.unix(timestamp).utc().format('MM/DD h:mm');
      }

      chart.subscribeCrosshairMove((param) => {
        if (
          param === undefined ||
          param.time === undefined ||
          param.point.x < 0 ||
          param.point.x > width ||
          param.point.y < 0 ||
          param.point.y > height
        ) {
          setLastBarText();
        } else {
          var price = param.seriesPrices.get(candleSeries).close
          const time = formatTimestampToTime(param.time);

          const formattedPrice = valueFormatter(price)
          toolTip.textContent = ''

          const priceDiv = document.createElement('div')
          priceDiv.style.fontSize = '22px'
          priceDiv.style.margin = '4px 0px'
          priceDiv.style.color = textColor

          if (typeof formattedPrice === 'string') {
            priceDiv.innerHTML = formattedPrice
          } else {
            ReactDOM.render(formattedPrice, priceDiv)
          }

          const timeSpan = document.createElement('span')
          timeSpan.style.fontSize = '12px'
          timeSpan.style.margin = '4px 6px'
          timeSpan.style.color = textColor
          timeSpan.textContent = time + ' UTC'

          toolTip.appendChild(priceDiv)
          toolTip.appendChild(timeSpan)

          if (dataFrequency) {
            updateTimeScaleOptions(dataFrequency)
          }
        }
      })

      function updateTimeScaleOptions(timeframe) {
        if (dataFrequency === 'hour') {
          chart.applyOptions({
            timeScale: {
              timeVisible: true,
              secondsVisible: false
            }
          })
        }
      }

      console.log(dataFrequency?.toString().toLowerCase())

      updateTimeScaleOptions('hour')

      chart.timeScale().fitContent()

      setChartCreated(chart)
    }
  // eslint-disable-next-line
  }, [chartCreated, formattedData, width, height, valueFormatter, base, margin, textColor])

  // responsiveness
  useEffect(() => {
    if (width) {
      chartCreated && chartCreated.resize(width, height)
      chartCreated && chartCreated.timeScale().scrollToPosition(0)
    }
  }, [chartCreated, height, width])

  return (
    <div>
      <div ref={ref} id="test-id" />
      <IconWrapper>
        <Play
          onClick={() => {
            chartCreated && chartCreated.timeScale().fitContent()
          }}
        />
      </IconWrapper>
    </div>
  )
}

export default CandleStickChart
